import React, { useState } from 'react';
import PasoPlegable from './PasoPlegable';
import PieDePaso from './PieDePaso';
import SelectorDeImagen from './SelectorDeImagen';
import SelectorDeRoles from './SelectorDeRoles';
import BusinessScheduleForm from '../BusinessScheduleForm';
import { rolesDe } from '../../lib/roles';

/**
 * Alta y edición de una persona del equipo, como recorrido de partes plegables
 * —el mismo acordeón que el detalle de un servicio y los datos básicos del
 * negocio—.
 *
 * POR QUÉ. Era una tirada de seis campos, y de los seis solo el nombre es
 * obligatorio: el resto —foto, roles, mail, teléfono, horario— son cosas que un
 * negocio completa cuando las tiene a mano, no de una sentada. Una lista plana
 * no dice cuáles de esas ya miraste: al volver a abrir la ficha de alguien, un
 * campo vacío y un campo que decidiste dejar vacío se ven igual. Partido en
 * cuatro, cada parte cerrada muestra lo que tiene cargado y lleva el tilde
 * cuando la diste por cerrada, que es lo que deja ver de un vistazo qué falta.
 *
 * EL TILDE ES "VISITADA Y COMPLETA", NO SOLO "COMPLETA". Es la regla que trae
 * EditableServiceCard y acá importa más todavía, porque tres de las cuatro
 * partes son opcionales: mirando solo los campos, "Contacto" nunca tendría el
 * tilde y "Horario" lo tendría desde el primer segundo (viene con el del
 * negocio puesto). En los dos casos el tilde dejaría de distinguir lo
 * terminado de lo que todavía no miraste, que es justo para lo que está.
 *
 * UNO SOLO PARA ALTA Y EDICIÓN. Los dos modales tenían los mismos seis campos
 * escritos dos veces, y ya habían empezado a separarse: el de alta pedía solo
 * el nombre y el de edición exigía nombre, mail y teléfono para poder guardar,
 * así que una persona cargada sin mail no se podía volver a editar nunca. La
 * única diferencia real es de qué horario parte cada uno, y eso entra en una
 * prop.
 */
export default function FormularioDePersona({
  valor,
  onChange,
  uid,
  catalogoDeRoles,
  rolesSugeridos,
  onBorrarRol,
  usosDelRol,
  scheduleDelNegocio,
  dayNames,
  // En el alta se elige entre heredar el horario del negocio o cargar uno
  // propio. En la edición no: la persona ya tiene su copia, y ofrecerle
  // "el mismo que el negocio" sería pisársela sin decirlo.
  conEleccionDeHorario = false,
  // Sufijo para los id de los campos. Los dos modales pueden existir en el
  // mismo documento, y dos <label for="staff-name"> apuntando al mismo id
  // hacen que tocar el de abajo enfoque el input de arriba.
  sufijoDeId = '',
  textoGuardar = 'Guardar',
  onGuardar,
  // El panel guarda contra el servidor y eso tarda; el onboarding sólo toca
  // estado en memoria. Sin esto, el botón del último paso queda vivo mientras
  // el pedido viaja y dos clicks mandan dos altas.
  guardando = false,
  error,
}) {
  const [parteAbierta, setParteAbierta] = useState('quien');
  const [visitadas, setVisitadas] = useState([]);

  const campo = (nombre) => `persona-${nombre}${sufijoDeId ? `-${sufijoDeId}` : ''}`;
  const set = (cambios) => onChange({ ...valor, ...cambios });

  const roles = rolesDe(valor);
  const horario = valor.schedule || scheduleDelNegocio || {};
  const diasAbiertos = Object.values(horario).filter((d) => d?.enabled && d.ranges?.length).length;
  // En el alta, mientras no se elija "uno propio", el horario es el del
  // negocio aunque `schedule` ya tenga una copia cargada.
  const heredaElHorario = conEleccionDeHorario && !valor.horarioPropio;

  const PARTES = [
    {
      id: 'quien',
      numero: 1,
      titulo: 'Quién es',
      descripcion: 'El nombre con el que la van a ver tus clientes, y su foto.',
      resumen: valor.name || 'Sin completar',
      // La única obligatoria de las cuatro.
      completa: () => Boolean(valor.name?.trim()),
    },
    {
      id: 'roles',
      numero: 2,
      titulo: 'Qué hace',
      descripcion: 'Los roles que cumple. Una persona puede tener más de uno.',
      resumen: roles.length ? roles.join(' · ') : 'Sin roles',
      completa: () => true,
    },
    {
      id: 'contacto',
      numero: 3,
      titulo: 'Contacto',
      descripcion: 'Para avisarle de sus turnos. Los dos son opcionales.',
      resumen: [valor.email, valor.phone].filter(Boolean).join(' · ') || 'Sin datos',
      completa: () => true,
    },
    {
      id: 'horario',
      numero: 4,
      titulo: 'Horario',
      descripcion: 'Cuándo atiende. Si no lo cambiás, es el del negocio.',
      resumen: heredaElHorario
        ? 'El mismo que el negocio'
        : diasAbiertos
        ? `Propio · ${diasAbiertos} ${diasAbiertos === 1 ? 'día' : 'días'}`
        : 'Sin días abiertos',
      completa: () => true,
    },
  ];

  const parteCompleta = (id) => {
    const parte = PARTES.find((p) => p.id === id);
    return visitadas.includes(id) && parte.completa();
  };

  const pie = (id) => {
    const i = PARTES.findIndex((p) => p.id === id);
    const siguiente = PARTES[i + 1];

    if (!siguiente) {
      return (
        <PieDePaso
          texto={textoGuardar}
          onClick={onGuardar}
          puedeAvanzar={Boolean(valor.name?.trim()) && !guardando}
          motivoBloqueo={guardando ? 'Guardando…' : 'Poné el nombre de la persona para guardar'}
        />
      );
    }
    return (
      <PieDePaso
        onClick={() => {
          setVisitadas((prev) => (prev.includes(id) ? prev : [...prev, id]));
          setParteAbierta(siguiente.id);
        }}
      />
    );
  };

  const plegable = (id) => {
    const p = PARTES.find((x) => x.id === id);
    return {
      numero: p.numero,
      titulo: p.titulo,
      descripcion: p.descripcion,
      resumen: p.resumen,
      completo: parteCompleta(id),
      abierto: parteAbierta === id,
      onToggle: () => setParteAbierta((actual) => (actual === id ? null : id)),
    };
  };

  return (
    <div className="formulario-de-persona flex flex-col gap-3">
      <PasoPlegable {...plegable('quien')}>
        {/* Nombre y foto uno al lado del otro. Apilados dejaban media caja vacía
            a la derecha del nombre: un input de texto no necesita 700px de ancho,
            y la foto es un avatar con un botón al lado, o sea otro bloque
            chato. En una fila los dos ocupan lo que les corresponde y la parte
            deja de ser el doble de alta de lo que su contenido pide.

            `sm:` y no siempre: por debajo de 640px no entran dos columnas sin
            que el botón de subir imagen se parta, así que ahí vuelven a
            apilarse. `min-w-0` en los dos porque un hijo de flex no baja de su
            contenido si no se lo permitís, y el input desbordaría la fila. */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-5">
          <div className="form-group min-w-0 flex-1">
            <label htmlFor={campo('nombre')}>
              Nombre <span className="campo-obligatorio">*</span>
            </label>
            <input
              id={campo('nombre')}
              type="text"
              value={valor.name || ''}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="Ej: María González"
              autoFocus
            />
          </div>

          {/* La foto es opcional: sin ella la página pública muestra las
              iniciales, que es lo que hizo siempre. */}
          <div className="form-group min-w-0 flex-1">
            <label>Foto <span className="campo-opcional">(opcional)</span></label>
            <SelectorDeImagen
              value={valor.foto}
              onChange={(foto) => set({ foto })}
              uid={uid}
              nombre={valor.name}
              prefijo="personal"
            />
          </div>
        </div>
        {pie('quien')}
      </PasoPlegable>

      {/* Los roles son etiquetas y no un campo de texto: las que el equipo ya
          usa se tocan, y las nuevas se escriben una sola vez. */}
      <PasoPlegable {...plegable('roles')}>
        <div className="form-group staff-roles">
          <label htmlFor={campo('roles')}>Roles <span className="campo-opcional">(opcional)</span></label>
          <SelectorDeRoles
            id={campo('roles')}
            value={valor.roles || []}
            onChange={(nuevos) => set({ roles: nuevos })}
            catalogo={catalogoDeRoles}
            sugeridos={rolesSugeridos}
            onBorrar={onBorrarRol}
            usosDelRol={usosDelRol}
          />
        </div>
        {pie('roles')}
      </PasoPlegable>

      <PasoPlegable {...plegable('contacto')}>
        {/* Los dos en la misma fila: son dos campos cortos y opcionales, y
            apilados hacían que la parte más prescindible del formulario fuera de
            las más altas. Mismo criterio que "Quién es". */}
        <div className="flex flex-col gap-4 sm:flex-row sm:gap-5">
          <div className="form-group min-w-0 flex-1">
            <label htmlFor={campo('email')}>Email <span className="campo-opcional">(opcional)</span></label>
            <input
              id={campo('email')}
              type="email"
              value={valor.email || ''}
              onChange={(e) => set({ email: e.target.value })}
              placeholder="maria@email.com"
            />
          </div>

          <div className="form-group min-w-0 flex-1">
            <label htmlFor={campo('telefono')}>Teléfono <span className="campo-opcional">(opcional)</span></label>
            <input
              id={campo('telefono')}
              type="tel"
              value={valor.phone || ''}
              onChange={(e) => set({ phone: e.target.value })}
              placeholder="+54 9 11 1234 5678"
            />
          </div>
        </div>
        {pie('contacto')}
      </PasoPlegable>

      <PasoPlegable {...plegable('horario')}>
        <div className="staff-horario">
          {conEleccionDeHorario && (
            <div className="staff-horario-opciones">
              <label className={!valor.horarioPropio ? 'activa' : ''}>
                <input
                  type="radio"
                  name={campo('horario-modo')}
                  checked={!valor.horarioPropio}
                  onChange={() => set({ horarioPropio: false })}
                />
                El mismo que el negocio
              </label>
              <label className={valor.horarioPropio ? 'activa' : ''}>
                <input
                  type="radio"
                  name={campo('horario-modo')}
                  checked={Boolean(valor.horarioPropio)}
                  onChange={() => set({ horarioPropio: true })}
                />
                Uno propio
              </label>
            </div>
          )}

          {!heredaElHorario && (
            <BusinessScheduleForm
              value={valor.schedule || scheduleDelNegocio}
              onChange={(schedule) => set({ schedule })}
              dayNames={dayNames}
            />
          )}
        </div>
        {pie('horario')}
      </PasoPlegable>

      {error && <p className="staff-form-error">{error}</p>}
    </div>
  );
}
